
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Mail, Save, ExternalLink } from 'lucide-react';
import { useSiteConfig } from '@/hooks/useSiteConfig';
import { useSiteConfigValue } from '@/hooks/useSiteConfigValue';
import { useToast } from '@/hooks/use-toast';

const AdminSiteConfigContactSettings = () => {
  const { toast } = useToast();
  const { updateConfiguration } = useSiteConfig();
  
  const { value: supportEmail, loading: supportEmailLoading, refetch: refetchSupportEmail } = useSiteConfigValue('support_email');
  const { value: contactPhone, loading: contactPhoneLoading, refetch: refetchContactPhone } = useSiteConfigValue('contact_phone');
  const { value: businessHours, loading: businessHoursLoading, refetch: refetchBusinessHours } = useSiteConfigValue('business_hours');
  const { value: helpCenterUrl, loading: helpCenterUrlLoading, refetch: refetchHelpCenterUrl } = useSiteConfigValue('help_center_url');
  
  const [saving, setSaving] = useState(false);
  const [tempValues, setTempValues] = useState({
    email: '',
    phone: '',
    hours: '',
    helpUrl: ''
  });

  React.useEffect(() => {
    if (supportEmail && !tempValues.email) setTempValues(prev => ({ ...prev, email: supportEmail }));
    if (contactPhone && !tempValues.phone) setTempValues(prev => ({ ...prev, phone: contactPhone }));
    if (businessHours && !tempValues.hours) setTempValues(prev => ({ ...prev, hours: businessHours }));
    if (helpCenterUrl && !tempValues.helpUrl) setTempValues(prev => ({ ...prev, helpUrl: helpCenterUrl }));
  }, [supportEmail, contactPhone, businessHours, helpCenterUrl, tempValues]);

  const handleUpdate = async (key: string, value: string, refetch: () => void) => {
    setSaving(true);
    try {
      await updateConfiguration(key, value);
      setTimeout(() => refetch(), 1000);
      toast({
        title: "Contact Setting Updated",
        description: `${key} has been updated successfully.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update contact setting.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const loading = supportEmailLoading || contactPhoneLoading || businessHoursLoading || helpCenterUrlLoading;

  if (loading) {
    return (
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center space-x-2">
            <Mail className="w-5 h-5" />
            <span>Contact & Support Settings</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-white/10 rounded w-1/3"></div>
            <div className="h-10 bg-white/10 rounded w-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/10 backdrop-blur-md border-white/20">
      <CardHeader>
        <CardTitle className="text-white flex items-center space-x-2">
          <Mail className="w-5 h-5" />
          <span>Contact & Support Settings</span>
          <Badge className="bg-purple-600/20 text-purple-300 border-purple-500/30">
            Customer Support
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Support Email */}
        <div className="p-4 rounded-lg bg-white/5 border border-white/10">
          <Label className="text-white">Support Email</Label>
          <div className="flex space-x-2 mt-2">
            <Input
              type="email"
              value={tempValues.email}
              onChange={(e) => setTempValues(prev => ({ ...prev, email: e.target.value }))}
              disabled={saving}
              placeholder="support@example.com"
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
            <Button
              onClick={() => handleUpdate('support_email', tempValues.email, refetchSupportEmail)}
              disabled={saving || !tempValues.email.trim() || tempValues.email === supportEmail}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Save className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-gray-400 mt-2">Primary support email for customer inquiries</p>
        </div>

        {/* Contact Phone */}
        <div className="p-4 rounded-lg bg-white/5 border border-white/10">
          <Label className="text-white">Contact Phone</Label>
          <div className="flex space-x-2 mt-2">
            <Input
              value={tempValues.phone}
              onChange={(e) => setTempValues(prev => ({ ...prev, phone: e.target.value }))}
              disabled={saving}
              placeholder="+1 (555) 123-4567"
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
            <Button
              onClick={() => handleUpdate('contact_phone', tempValues.phone, refetchContactPhone)}
              disabled={saving || tempValues.phone === contactPhone}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Save className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-gray-400 mt-2">Contact phone number for support</p>
        </div>

        {/* Business Hours */}
        <div className="p-4 rounded-lg bg-white/5 border border-white/10">
          <Label className="text-white">Business Hours</Label>
          <div className="flex space-x-2 mt-2">
            <Input
              value={tempValues.hours}
              onChange={(e) => setTempValues(prev => ({ ...prev, hours: e.target.value }))}
              disabled={saving}
              placeholder="9 AM - 5 PM EST"
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
            <Button
              onClick={() => handleUpdate('business_hours', tempValues.hours, refetchBusinessHours)}
              disabled={saving || tempValues.hours === businessHours}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Save className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-gray-400 mt-2">Support availability hours</p>
        </div>

        {/* Help Center URL */}
        <div className="p-4 rounded-lg bg-white/5 border border-white/10">
          <Label className="text-white">Help Center URL</Label>
          <div className="flex space-x-2 mt-2">
            <Input
              type="url"
              value={tempValues.helpUrl}
              onChange={(e) => setTempValues(prev => ({ ...prev, helpUrl: e.target.value }))}
              disabled={saving}
              placeholder="https://help.example.com"
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
            {tempValues.helpUrl && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.open(tempValues.helpUrl, '_blank')}
                className="text-blue-400 hover:text-blue-300"
                disabled={saving}
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
            )}
            <Button
              onClick={() => handleUpdate('help_center_url', tempValues.helpUrl, refetchHelpCenterUrl)}
              disabled={saving || tempValues.helpUrl === helpCenterUrl}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Save className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-gray-400 mt-2">Link to documentation and help resources</p>
        </div>

        {/* Debug Info */}
        <div className="text-xs text-gray-500 p-3 bg-black/20 rounded">
          <div>Email: {supportEmail}</div>
          <div>Phone: {contactPhone}</div>
          <div>Hours: {businessHours}</div>
          <div>Help URL: {helpCenterUrl}</div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminSiteConfigContactSettings;
